import {
  WorkOrderType,
  WorkOrderStatus,
  WorkOrderMilestoneName,
  getCountryAbbreviationFromWorkOrderType,
  EuCountryCodes,
} from '@xbcb/work-order-types';
import { AppRecordKebabMenuItem } from 'routes';
import { pascalCase } from 'change-case';
import { getRecordType } from '@xbcb/core';
import { RecordType } from '@xbcb/shared-types';
import { AppRecordOnConfirmPayload } from '../../types';
import { executeMutation } from 'libs/executeMutation';
import { safeGetMessage, setModal } from '@xbcb/ui-utils';
import { ModalKey } from '@xbcb/ui-types';
import {
  createWorkOrderMilestoneMutation,
  updateEuCustomsEntry,
} from 'libs/sharedQueries';

import { checkAccess, UserPermissions } from '@xbcb/client-utils';
import { WorkOrderBadge } from '@xbcb/work-order-types/dist/enums/workOrder/badge';
import { v4 } from 'uuid';
import { isRecordCanceled, isRecordDeleted } from 'libs/workOrderConditions';
import { fetchLatestRecordAndExecuteMutation } from 'libs/fetchLatestRecordAndExecuteMutation';
import { isEuCustomsDeclaration } from '@xbcb/work-order-utils/dist/isEuCustomsDeclaration';
import { updateCustomsDeclaration } from '../workOrderRoutes/customsDeclaration/customsDeclaration.query';
import { isManageAnalysisBadgesEnabled } from 'libs/featureFlags/manageAnalysisBadgesFeatureFlags';

const classificationBadges = [
  {
    badge: WorkOrderBadge.BIXD,
    milestone: WorkOrderMilestoneName.BIXD_WORK_ORDER_CLASSIFIED,
    declassifyMilestone: WorkOrderMilestoneName.BIXD_WORK_ORDER_DECLASSIFIED,
  },
  {
    badge: WorkOrderBadge.SAFEACT,
    milestone: WorkOrderMilestoneName.SAFEACT_WORK_ORDER_CLASSIFIED,
    declassifyMilestone: WorkOrderMilestoneName.SAFEACT_WORK_ORDER_DECLASSIFIED,
  },
  {
    badge: WorkOrderBadge.MULTIVAT,
    milestone: WorkOrderMilestoneName.MULTIVAT_WORK_ORDER_CLASSIFIED,
    declassifyMilestone:
      WorkOrderMilestoneName.MULTIVAT_WORK_ORDER_DECLASSIFIED,
  },
];

const getCurrentClassificationBadges = (workOrder: {
  badges?: WorkOrderBadge[];
}): WorkOrderBadge[] => {
  const currentBadges = workOrder?.badges || [];
  const classificationBadgeTypes = classificationBadges.map((c) => c.badge);
  return currentBadges.filter((badge: WorkOrderBadge) =>
    classificationBadgeTypes.includes(badge),
  );
};

export const manageAnalysisBadgesMenuItem: AppRecordKebabMenuItem = {
  key: 'manageAnalysisBadges',
  text: ({ appRecordBundle }) =>
    safeGetMessage(appRecordBundle, 'manage_analysis_badges'),
  show: ({ existingRecord, currentUser }) => {
    if (!isManageAnalysisBadgesEnabled()) return false;
    if (!existingRecord?.id || !existingRecord?.status) {
      return false;
    }
    const recordType = getRecordType(existingRecord.id) as RecordType;
    return (
      ![
        WorkOrderStatus.NOT_STARTED,
        WorkOrderStatus.COMPLETED,
        WorkOrderStatus.REPLACED,
      ].includes(existingRecord.status) &&
      !isRecordCanceled(existingRecord) &&
      !isRecordDeleted(existingRecord) &&
      checkAccess(currentUser, recordType, UserPermissions.UPDATE)
    );
  },
  action: ({ dispatch, record, appRecordBundle }) => {
    const existingRecord = record;
    const recordType = getRecordType(existingRecord.id) as RecordType;
    dispatch(
      setModal({
        key: ModalKey.EDIT_WORK_ORDER_ANALYSIS_BADGES,
        props: {
          visible: true,
          sendConfirmationDecision: async (
            payload: AppRecordOnConfirmPayload & {
              selectedBadges?: WorkOrderBadge[];
            },
          ) => {
            if (payload.canceled) {
              return;
            }

            const workOrder = existingRecord;
            const selectedBadges = payload.selectedBadges || [];
            const currentBadges = workOrder.badges || [];
            const classificationBadgeTypes = classificationBadges.map(
              (c) => c.badge,
            );
            const nonClassificationBadges = currentBadges.filter(
              (badge: WorkOrderBadge) =>
                !classificationBadgeTypes.includes(badge),
            );
            const updatedBadges = [
              ...nonClassificationBadges,
              ...selectedBadges,
            ];

            const currentClassificationBadges = currentBadges.filter(
              (badge: WorkOrderBadge) =>
                classificationBadgeTypes.includes(badge),
            );
            const badgesToAdd = selectedBadges.filter(
              (badge: WorkOrderBadge) =>
                !currentClassificationBadges.includes(badge),
            );
            const badgesToRemove = currentClassificationBadges.filter(
              (badge: WorkOrderBadge) => !selectedBadges.includes(badge),
            );

            for (const badge of badgesToAdd) {
              const classification = classificationBadges.find(
                (c) => c.badge === badge,
              );
              if (classification) {
                await executeMutation({
                  mutation: createWorkOrderMilestoneMutation,
                  successMessage: safeGetMessage(
                    appRecordBundle,
                    'classification_milestone_published',
                    { badge },
                  ),
                  variables: {
                    idempotencyKey: v4(),
                    input: {
                      allowDuplicate: true,
                      name: classification.milestone,
                      workOrder: {
                        id: workOrder.id,
                        version: workOrder.version,
                      },
                    },
                  },
                });
              }
            }

            for (const badge of badgesToRemove) {
              const classification = classificationBadges.find(
                (c) => c.badge === badge,
              );
              if (classification) {
                await executeMutation({
                  mutation: createWorkOrderMilestoneMutation,
                  successMessage: safeGetMessage(
                    appRecordBundle,
                    'declassification_milestone_published',
                    { badge },
                  ),
                  variables: {
                    idempotencyKey: v4(),
                    input: {
                      allowDuplicate: true,
                      name: classification.declassifyMilestone,
                      workOrder: {
                        id: workOrder.id,
                        version: workOrder.version,
                      },
                    },
                  },
                });
              }
            }

            await fetchLatestRecordAndExecuteMutation({
              id: workOrder.id,
              recordType,
              mutation: isEuCustomsDeclaration(workOrder)
                ? updateCustomsDeclaration
                : updateEuCustomsEntry(
                    getCountryAbbreviationFromWorkOrderType(
                      pascalCase(recordType) as WorkOrderType,
                    ) as EuCountryCodes,
                  ),
              constructVariablesWithIdVersion: (id, version) => ({
                id: id,
                version: version,
                input: {
                  badges: updatedBadges,
                },
              }),
            });
          },
          workOrders: [existingRecord],
          workOrderType: recordType,
          availableClassifications: classificationBadges,
          currentClassifications:
            getCurrentClassificationBadges(existingRecord),
        },
      }),
    );
  },
};
